/**
 * Copyright 2025 Amazon.com, Inc. and its affiliates. All Rights Reserved.
 *
 * Licensed under the Amazon Software License (the "License").
 * You may not use this file except in compliance with the License.
 * A copy of the License is located at
 *
 *   http://aws.amazon.com/asl/
 *
 * or in the "license" file accompanying this file. This file is distributed
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the License for the specific language governing
 * permissions and limitations under the License.
 */

import AudioPlayer from "../play/AudioPlayer";
import ChatHistoryManager from "../util/ChatHistoryManager.js";
import {
  getValidWSAIAvatarKey,
  getWSAIAvatarVoiceId
} from "@/_models/virtualStaging/AIAvater/lib/services/websocketAuthManager";

export class WebSocketEventManager {
  constructor() {
    const wsKey = getValidWSAIAvatarKey('Enter your WebSocket AI Avatar key (valid for 24 hours)');
    this.customVoiceId = getWSAIAvatarVoiceId();

    if (!wsKey) {
      console.warn('AI Avatar initialization cancelled - no WebSocket key provided');
    }
    this.wsUrl = wsKey ? `https://d1kx92ni970aje.cloudfront.net/api/api/${wsKey}` : process.env.NEXT_PUBLIC_WS_AI_AVATAR;
    this.promptName = null;
    this.audioContentName = null;
    this.audioPlayer = new AudioPlayer();
    this.audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    this.currentAudioConfig = null;
    this.isProcessing = false;
    this.seenChunks = new Set();
    this.customSystemPrompt = null;

    // Message handling properties
    this.messageBuffer = {};

    this.chat = { history: [] };
    this.chatRef = { current: this.chat };

    this.chatHistoryManager = ChatHistoryManager.getInstance(
      this.chatRef,
      (newChat) => {
        this.chat = { ...newChat };
        this.chatRef.current = this.chat;
        // Call the update transcript callback if defined
        if (this.onUpdateTranscript) {
          this.onUpdateTranscript(this.chat.history, this.chat.content);
        }
      }
    );
    this.audioPlayer.addEventListener("onPlaybackStarted", () => {
      this.onAudioResponseStarted && this.onAudioResponseStarted();
    });
    this.audioPlayer.addEventListener("onPlaybackFinished", () => {
      this.onAudioResponseFinished && this.onAudioResponseFinished();
    });
    this.connect();
  }

  // Set custom system prompt
  setSystemPrompt(prompt) {
    if (prompt && prompt.trim()) {
      this.customSystemPrompt = prompt.trim();
      console.log("Custom system prompt set");
    }
  }

  // Callback handlers that can be set from main.js
  onUpdateTranscript = null;
  onUpdateStatus = null;
  onAudioReceived = null;
  onAudioResponseStarted = null;
  onAudioResponseFinished = null;


  connect() {
    if (this.socket) {
      this.socket.close();
    }
    this.socket = new WebSocket(this.wsUrl);
    this.setupSocketListeners();
  }

  setupSocketListeners() {
    this.socket.onopen = () => {
      console.log("WebSocket Connected");
      this.updateStatus("Connected", "connected");
      this.isProcessing = true;
      this.startSession();
      // Listening user
      this.audioPlayer.start();
    };

    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleMessage(data);
      } catch (e) {
        console.error("Error parsing message:", e, "Raw data:", event.data);
      }
    };

    this.socket.onerror = (error) => {
      console.error("WebSocket Error:", error);
      this.updateStatus("Connection error", "error");
      this.isProcessing = false;
    };

    this.socket.onclose = (event) => {
      console.log("WebSocket Disconnected", event);
      this.updateStatus("Disconnected", "disconnected");
      this.isProcessing = false;
      this.audioPlayer.stop();
      if (this.isProcessing) {
        console.log("Attempting to reconnect...");
        setTimeout(() => this.connect(), 1000);
      }
    };
  }

  async sendEvent(event) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error(
        "WebSocket is not open. Current state:",
        this.socket?.readyState
      );
      return;
    }

    try {
      this.socket.send(JSON.stringify(event));
    } catch (error) {
      console.error("Error sending event:", error);
      this.updateStatus("Error sending message", "error");
    }
  }

  handleMessage(data) {
    if (!data.event) {
      console.error("Received message without event:", data);
      return;
    }

    const event = data.event;
    // console.log("Event received:", event);

    try {
      // Handle session events
      if (event.sessionStart) {
        console.log("Session start received");
      }
      // Handle prompt events
      else if (event.promptStart) {
        console.log("Prompt start received");
        this.promptName = event.promptStart.promptName;
      }
      // Handle content start
      else if (event.contentStart) {
        // console.log("Content start received:", event.contentStart.type);
        if (event.contentStart.type === "AUDIO") {
          this.currentAudioConfig = event.contentStart.audioOutputConfiguration;
          this.audioBuffer = [];
        } else if (event.contentStart.type === "TEXT") {
          // Reset message buffer for new text content
          this.messageBuffer = {};
        }
      }
      // Handle text output
      else if (event.textOutput) {
        // console.log("Text output received");
        const role = event.textOutput.role;
        let content = event.textOutput.content;

        // Process speculative content if needed
        if (role === "ASSISTANT" && content.startsWith("Speculative: ")) {
          content = content.slice(13);
        }

        // Skip duplicate chunks
        if (this.seenChunks.has(content)) {
          console.log("Skipping duplicate chunk");
          return;
        }
        this.seenChunks.add(content);

        // Buffer text by role
        if (!this.messageBuffer[role]) {
          this.messageBuffer[role] = content;
        } else {
          // Append to existing content
          this.messageBuffer[role] += content;
        }
      }
      // Handle audio output
      else if (event.audioOutput) {
        // console.log("Audio output received");
        if (this.currentAudioConfig) {
          const audioData = this.base64ToFloat32Array(
            event.audioOutput.content
          );
          // console.log("websocketEvents:Audio output received", audioData);

          this.audioPlayer.playAudio(audioData);

          if (this.onAudioReceived) {
            this.onAudioReceived(audioData);
          }
        }
      }
      // Handle content end
      else if (event.contentEnd) {
        // console.log("Content end received:", event.contentEnd.type);
        const contentType = event.contentEnd.type;

        if (event.contentEnd.stopReason === "INTERRUPTED") {
          // console.log("Content was interrupted by user");
          // Use the existing bargeIn method
          this.audioPlayer.bargeIn();

          // You might want to update the UI to show the interruption
          this.updateStatus("User interrupted", "interrupted");
        }

        if (contentType === "TEXT") {
          // Process buffered text messages
          for (const [role, message] of Object.entries(this.messageBuffer)) {
            if (message && message.trim()) {
              this.chatHistoryManager.addTextMessage({
                role: role,
                message: message,
              });
            }
          }
          this.messageBuffer = {};
        }
      }
      // Handle tool use events
      else if (event.toolUse) {
        console.log("Tool use event received:", event.toolUse.toolName);
      }
      // Handle prompt end
      else if (event.promptEnd) {
        console.log("Prompt end received");
      }
      // Handle session end
      else if (event.sessionEnd) {
        console.log("Session end received");
      } else {
        console.warn("Unknown event type received:", Object.keys(event)[0]);
      }
    } catch (error) {
      console.error("Error processing message:", error);
      console.error("Event data:", event);
    }
  }

  updateStatus(message, className) {
    // Call the update status callback if defined
    if (this.onUpdateStatus) {
      this.onUpdateStatus(message, className);
    }
  }

  base64ToFloat32Array(base64String) {
    const binaryString = window.atob(base64String);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const int16Array = new Int16Array(bytes.buffer);
    const float32Array = new Float32Array(int16Array.length);
    for (let i = 0; i < int16Array.length; i++) {
      float32Array[i] = int16Array[i] / 32768.0;
    }

    return float32Array;
  }

  startSession() {
    console.log("Starting session...");
    const sessionStartEvent = {
      event: {
        sessionStart: {
          inferenceConfiguration: {
            maxTokens: 1024,
            topP: 0.95,
            temperature: 0.7,
          },
        },
      },
    };
    this.sendEvent(sessionStartEvent);
    this.startPrompt();
  }

  startPrompt() {
    this.promptName = crypto.randomUUID();
    const promptStartEvent = {
      event: {
        promptStart: {
          promptName: this.promptName,
          textOutputConfiguration: {
            mediaType: "text/plain",
          },
          audioOutputConfiguration: {
            mediaType: "audio/lpcm",
            sampleRateHertz: 24000,
            sampleSizeBits: 16,
            channelCount: 1,
            voiceId: this.customVoiceId || "tiffany", // Match the voice ID in your backend
            encoding: "base64",
            audioType: "SPEECH",
          },
          toolUseOutputConfiguration: {
            mediaType: "application/json",
          },
          toolConfiguration: {
            tools: [
              {
                toolSpec: {
                  name: "lookup",
                  description:
                    "Runs query against a knowledge base to retrieve information.",
                  inputSchema: {
                    json: JSON.stringify({
                      $schema: "http://json-schema.org/draft-07/schema#",
                      type: "object",
                      properties: {
                        query: {
                          type: "string",
                          description: "the query to search",
                        },
                      },
                      required: ["query"],
                    }),
                  },
                },
              },
            ],
          },
        },
      },
    };

    this.sendEvent(promptStartEvent);
    this.sendSystemPrompt();
  }

  sendSystemPrompt() {
    const systemContentName = crypto.randomUUID();
    const contentStartEvent = {
      event: {
        contentStart: {
          promptName: this.promptName,
          contentName: systemContentName,
          type: "TEXT",
          interactive: true,
          textInputConfiguration: {
            mediaType: "text/plain",
          },
        },
      },
    };
    this.sendEvent(contentStartEvent);

    // System prompt - use custom prompt if available, otherwise default
    const defaultPrompt = `
    # Amazon Fulfillment & Robotics Assistant

      You are a specialized assistant with expertise in Amazon fulfillment centers and robotics technologies. Your primary purpose is to provide accurate, detailed information by referencing the knowledge base whenever possible.

      ## CORE INSTRUCTIONS

      1. ALWAYS prioritize information from the knowledge base over general knowledge.
      2. When answering questions, cite specific sections from the knowledge base.
      3. If information exists in the knowledge base, use it as your primary source.
      4. When citing information, specify which document and section it comes from.
      5. If asked about something not covered in the knowledge base, clearly state this limitation.

      ## KNOWLEDGE BASE USAGE

      - Begin responses by checking the knowledge base for relevant information
      - Quote directly from the knowledge base when appropriate
      - Structure answers to highlight knowledge base information

      ## RESPONSE FORMAT

      When answering questions:
      1. Start by identifying relevant knowledge base information
      2. Provide the information from the knowledge base
      3. Only add general knowledge if necessary and clearly mark it as supplementary
      4. Keep the responses conversational, avoid URL's or other none native speech constructs
      5. Limit the response to 4 sentences or less

      ## LIMITATIONS

      - You should acknowledge when information isn't in the knowledge base
      - Do not speculate beyond what the knowledge base contains
      - Clarify when you're providing information from general knowledge
      - Recommend consulting specific knowledge base documents for further information
      - Do not include references about the knowledge base in your output, this will be a conversational bot, users will not be able to access the knowledge base themselves

      ## TOPICS TO ADDRESS

      - Amazon fulfillment center operations and logistics
      - Robotics technologies used in Amazon facilities
      - Automation systems and their implementation
      - Warehouse management systems
      - Order processing workflows
      - Inventory management techniques
      - Safety protocols in automated environments
      - Future developments in fulfillment robotics
      - Integration of AI with physical automation systems

      Remember: Your primary function is to accurately represent the information contained in the knowledge base, with proper attribution and minimal speculation beyond what is documented.
    `;
    const systemPrompt = this.customSystemPrompt || defaultPrompt;

    console.log(
      "Sending system prompt:",
      systemPrompt.substring(0, 100) + "..."
    );

    const textInputEvent = {
      event: {
        textInput: {
          promptName: this.promptName,
          contentName: systemContentName,
          content: systemPrompt,
          role: "SYSTEM",
        },
      },
    };
    this.sendEvent(textInputEvent);

    const contentEndEvent = {
      event: {
        contentEnd: {
          promptName: this.promptName,
          contentName: systemContentName,
        },
      },
    };
    this.sendEvent(contentEndEvent);

    this.startAudioContent();
  }

  startAudioContent() {
    this.audioContentName = crypto.randomUUID();
    const contentStartEvent = {
      event: {
        contentStart: {
          promptName: this.promptName,
          contentName: this.audioContentName,
          type: "AUDIO",
          role: "USER",
          interactive: true,
          audioInputConfiguration: {
            mediaType: "audio/lpcm",
            sampleRateHertz: 16000,
            sampleSizeBits: 16,
            channelCount: 1,
            audioType: "SPEECH",
            encoding: "base64",
          },
        },
      },
    };
    this.sendEvent(contentStartEvent);
  }

  sendAudioChunk(base64AudioData) {
    if (!this.promptName || !this.audioContentName) {
      console.error(
        "Cannot send audio chunk - missing promptName or audioContentName"
      );
      return;
    }

    const audioInputEvent = {
      event: {
        audioInput: {
          promptName: this.promptName,
          contentName: this.audioContentName,
          content: base64AudioData,
          role: "USER",
        },
      },
    };
    this.sendEvent(audioInputEvent);
  }

  endContent() {
    const contentEndEvent = {
      event: {
        contentEnd: {
          promptName: this.promptName,
          contentName: this.audioContentName,
        },
      },
    };
    this.sendEvent(contentEndEvent);
  }

  endPrompt() {
    const promptEndEvent = {
      event: {
        promptEnd: {
          promptName: this.promptName,
        },
      },
    };
    this.sendEvent(promptEndEvent);
  }

  endSession() {
    const sessionEndEvent = {
      event: {
        sessionEnd: {},
      },
    };
    this.sendEvent(sessionEndEvent);
    this.socket.close();
  }

  cleanup() {
    this.isProcessing = false;
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      try {
        if (this.audioContentName && this.promptName) {
          this.endContent();
          this.endPrompt();
        }
        this.endSession();
      } catch (error) {
        console.error("Error during cleanup:", error);
      }
    }
    this.chatHistoryManager.endConversation();
  }

  // Keep just the basic methods for speech detection
  startUserTalking() {
    console.log("User started talking");
  }

  stopUserTalking() {
    console.log("User stopped talking");
  }
}
